type FetchOptions = {
    headers?: Record<string, string>;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: any;
    signal?: AbortSignal;
    _isRetry?: boolean; // Internal flag to prevent infinite retry loops
};

export const fetchWithAuth = async (url: string, options: FetchOptions = {}, session: any) => {
    if (!session || !session.getToken) {
        throw new Error("Session is invalid or getToken method is unavailable.");
    }

    // Always get a fresh token for each request to minimize expiration issues
    const token = await session.getToken();

    // Adding the Authorization header to the existing headers
    const headersWithAuth = {
        'Accept': 'application/json',
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'x-session-id': session && session.id,
    } as Record<string, string>;

    try {        
        const response = await fetch(url, {
            ...options,
            headers: headersWithAuth,
            credentials: 'include',
            signal: options.signal
        });

        // Enhanced 401 handling with automatic token refresh and retry
        if (response.status === 401 && !options._isRetry) {
            console.log("🔄 Token expired (401), attempting to refresh and retry...");
            
            try {
                // Force refresh the token by skipping cache
                const freshToken = await session.getToken({ skipCache: true });
                console.log("✅ Successfully refreshed token, retrying request...");
                
                // Retry the request with the fresh token
                const retryResponse = await fetch(url, {
                    ...options,
                    headers: {
                        ...headersWithAuth,
                        'Authorization': `Bearer ${freshToken}`,
                    },
                    credentials: 'include',
                    signal: options.signal
                });

                // If retry also fails with 401, the session is truly invalid
                if (retryResponse.status === 401) {
                    console.error("❌ Token refresh failed - session is invalid");
                    throw new Error("Session expired. Please refresh the page and log in again.");
                }

                console.log("✅ Request succeeded after token refresh");
                return retryResponse;
                
            } catch (refreshError) {
                console.error("❌ Failed to refresh token:", refreshError);
                throw new Error("Session expired. Please refresh the page and log in again.");
            }
        }

        // Handle other 401 cases (when it's already a retry)
        if (response.status === 401 && options._isRetry) {
            throw new Error("Session expired. Please refresh the page and log in again.");
        }

        // Handle other HTTP errors
        if (!response.ok && response.status !== 401) {
            console.warn(`⚠️ Request failed with status ${response.status}: ${response.statusText}`);
        }

        return response;
        
    } catch (error: any) {
        // Suppress noisy logs for intentional aborts during rapid UI updates/search
        if (error && (error.name === 'AbortError' || error.code === 'ABORT_ERR')) {
            throw error;
        }
        
        // Enhanced error logging for debugging
        console.error("❌ Fetch error:", {
            url,
            method: options.method || 'GET',
            error: error.message,
            isRetry: options._isRetry || false
        });
        
        throw error;
    }
};
